import React, { useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { TabPanel } from 'primereact/tabview'

import { ChargePoint, useOcppCommandDataTransferMutation } from '../../redux/api/web-api'
import { ChargePointDawnControlWidget } from '../../../lib/charge-point-dawn-control-widget'
import { ChargePointOcppAuraControlWidget } from '../../../lib/charge-point-ocpp-aura-control-widget'
import { ChargePointOcppHaloControlWidget } from '../../../lib/charge-point-ocpp-halo-control-widget'
import isAura from '../../../lib/isAura'
import isDawn from '../../../lib/isDawn'
import isHalo from '../../../lib/isHalo'
import { ChargePointOcppLogsWidget } from '../../components/charge-point-ocpp-logs-widget'
import { ChargepointLatestChargingSessionsWidget } from '../../components/charge-point-latest-charging-sessions-widget'
import {
    isAdminDomain,
    isLandingPage,
    isPartnerDomain,
    isSupportDomain,
    isUserappDomain,
} from '../../../lib/currentDomain'
import useRemoteControlAutoRefresh from '../../hooks/useRemoteControlAutoRefresh'
import { ChargepointDetailsWidget } from '../../features/chargepoint-details-widget'
import { ChargePointMaintenanceLogsWidget } from '../../components/charge-point-maintenance-logs-widget'
import { ChargePointPartnerInfoWidget } from '../../features/charge-point-partner-info-widget/charge-point-partner-info-widget'
import isLuna from '../../../lib/isLuna'
import { ChargePointLunaControlWidget } from '../../../lib/charge-point-luna-control-widget'
import useText from '../../hooks/useText'
import { ChargePointOverviewWidget } from '../../components/charge-point-overview-widget'
import { ChargePointStatusWidget } from '../../components/charge-point-status-widget'
import { ChargePointSchedulesPage } from '../../../ui/ChargePointSchedulesPage'
import isOnline from '../../../lib/isOnline'
import { ChargepointIntegrationsWidget } from '../../features/charge-point-integrations-widget'
import { OcppElectricalSettings } from '../../../support/components/ocpp-electrical-settings'
import { ChargePointPropertiesWidget } from '../../../support/features/charge-point-properties-widget'
import { ChargePointOcppConfigWidget } from '../../../support/components/charge-point-ocpp-config-widget'
import { ChargePointOcppCommandsWidget } from '../../../support/components/charge-point-ocpp-commands-widget'
import { InteractionLogsWidget } from '../../../support/components/interaction-logs-widget'
import { ChargePointAlarmsWidget } from '../../components/charge-point-alarms-widget'

import { OcppChargePointTabView } from './ocpp-charge-point-tab-view'

export const SContainer = styled.div`
    margin: 0 -15px -15px;
    padding: 0 15px 30px;
`

export const SWidgetGroupsContainer = styled.div`
    display: flex;
    gap: 30px;
    justify-content: space-between;

    @media (max-width: 990px) {
        flex-direction: column;
        gap: 20px;
    }
`

export const SWidgetsGroup = styled.div<{ width?: string }>`
    display: flex;
    flex-direction: column;
    width: ${({ width }) => width};
    width: 50%;

    @media (max-width: 990px) {
        width: 100%;
    }
`

export interface ChargePointOcppPageProps {
    chargePoint: ChargePoint
    isLoading: boolean
}

export function ChargePointOcppPage({ chargePoint, isLoading }: ChargePointOcppPageProps): React.ReactElement | null {
    const skipRemoteControlAutoRefresh = isUserappDomain && isLandingPage(window.location.pathname)
    const remoteControlData = useRemoteControlAutoRefresh(chargePoint.id, skipRemoteControlAutoRefresh)

    const text = useText()

    const [dataTransferRequest] = useOcppCommandDataTransferMutation()
    const dataTransfer = useCallback(
        (messageId: string) => {
            if (isPartnerDomain || isSupportDomain) {
                void dataTransferRequest({
                    chargePointId: chargePoint.id,
                    dataTransferReq: {
                        vendorId: 'com.chargeamps',
                        messageId,
                    },
                }).unwrap()
            }
        },
        [chargePoint.id, dataTransferRequest]
    )

    const chargerIsOnline = isOnline(chargePoint)

    useEffect(() => {
        if (chargerIsOnline) {
            dataTransfer(`networkStatus`)
        }
    }, [chargerIsOnline, dataTransfer])
    return (
        <OcppChargePointTabView>
            <TabPanel key="properties" header={text('lblPropertiesWidget')} leftIcon="pi pi-list mr-2">
                <div className="grid">
                    <div className="lg:col-6 col-12">
                        <ChargePointPropertiesWidget chargePoint={chargePoint} disabled={isLoading} />
                    </div>
                    <div className="lg:col-6 col-12">
                        {remoteControlData && (
                            <>
                                <ChargePointOverviewWidget chargePoint={remoteControlData} />

                                <div className="mt-4">
                                    <ChargePointStatusWidget chargePoint={remoteControlData} />
                                </div>

                                <div className="mt-4">
                                    {isHalo(chargePoint) && (
                                        <ChargePointOcppHaloControlWidget
                                            chargePoint={remoteControlData}
                                            isReadOnly={false}
                                        />
                                    )}
                                    {isAura(chargePoint) && (
                                        <ChargePointOcppAuraControlWidget
                                            chargePoint={remoteControlData}
                                            isReadOnly={false}
                                        />
                                    )}
                                    {isDawn(chargePoint) && (
                                        <ChargePointDawnControlWidget
                                            chargePoint={remoteControlData}
                                            isReadOnly={false}
                                        />
                                    )}
                                    {isLuna(chargePoint) && (
                                        <ChargePointLunaControlWidget chargePoint={remoteControlData} />
                                    )}
                                </div>
                            </>
                        )}

                        {isSupportDomain && (
                            <div className="mt-4">
                                <InteractionLogsWidget chargePointId={chargePoint.id} />
                            </div>
                        )}
                    </div>
                </div>
            </TabPanel>

            {(isSupportDomain || isPartnerDomain) && (
                <TabPanel key="integrations" header={text('btnIntegrations')} leftIcon="pi pi-cog mr-2">
                    <ChargepointIntegrationsWidget chargePoint={chargePoint} />
                </TabPanel>
            )}

            {!isSupportDomain && (
                <TabPanel key="charger-settings" header={text('lblChargerSettings')} leftIcon="pi pi-cog mr-2">
                    <ChargepointDetailsWidget chargePoint={chargePoint} />
                </TabPanel>
            )}

            <TabPanel key="electrical-settings" header={text('lblElectricalSettings')} leftIcon="pi pi-bolt mr-2">
                <div className="grid">
                    <div className=" col-12">
                        <OcppElectricalSettings chargePoint={remoteControlData ?? chargePoint} />
                    </div>
                </div>
            </TabPanel>

            {!isAdminDomain && (
                <TabPanel key="ocpp-logs" header={text('lblOcppLogsWidget')} leftIcon="pi pi-receipt mr-2">
                    <ChargePointOcppLogsWidget
                        chargePointId={chargePoint.id}
                        showOcppRelayLogs={chargePoint?.ocppEnabled}
                    />
                </TabPanel>
            )}

            {((isAdminDomain && !chargePoint?.ocppEnabled) || isSupportDomain) && (
                <TabPanel key="schedules" header={text('lblSchedules')} leftIcon="pi pi-calendar mr-2">
                    <ChargePointSchedulesPage />
                </TabPanel>
            )}

            {(!isAdminDomain || (isPartnerDomain && !chargePoint?.ocppEnabled)) && (
                <TabPanel
                    key="charging-sessions"
                    header={text('lblChargingSessionsWidget')}
                    leftIcon="pi pi-wave-pulse mr-2"
                >
                    <ChargepointLatestChargingSessionsWidget
                        chargePointId={chargePoint.id}
                        useFilter={true}
                        deleteEnabled={!isPartnerDomain}
                    />
                </TabPanel>
            )}

            {isSupportDomain && (
                <TabPanel key="ocpp-commands" header={text('lblOcppCommandsWidget')} leftIcon="pi pi-file-import mr-2">
                    <div className="grid">
                        <div className="lg:col-6 col-12">
                            <ChargePointOcppCommandsWidget chargePoint={chargePoint} />
                        </div>
                        <div className="lg:col-6 col-12">
                            <ChargePointOcppConfigWidget chargePoint={remoteControlData ?? chargePoint} />
                        </div>
                    </div>
                </TabPanel>
            )}
            {((isPartnerDomain && !chargePoint?.ocppEnabled) || isSupportDomain) && (
                <TabPanel key="alerts" header={text('lblAlerts')} leftIcon="pi pi-bell mr-2 ">
                    <ChargePointAlarmsWidget />

                    <div className="mt-3">
                        <ChargePointMaintenanceLogsWidget />
                    </div>
                </TabPanel>
            )}
            {isAdminDomain && (
                <TabPanel key="support" header={text('lbllinkSupport')} leftIcon="pi pi-phone mr-2">
                    <ChargePointPartnerInfoWidget chargePointId={chargePoint.id} />
                </TabPanel>
            )}
        </OcppChargePointTabView>
    )
}
